import * as React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useQueryClient } from '@tanstack/react-query';
import { RouteComponentProps } from 'react-router';
import { Heading, SectionWrapper, SheetLayoutContent, SheetLayoutHeader } from '../../../../brenger-shared-ui';
import { useTypedSelector } from '../../../../hooks';
import { CacheKey, StopType } from '../../../../types';
import { logException } from '../../../../utils/basics';
import { priceAsString } from '../../../../utils/price';
import FooterLayout from '../../containers/FooterLayout';
import {
  getContactPerson,
  getFlowTitleByStepAndSituation,
  getPickupSituation,
  getProductPaymentAmount,
  getProductPaymentOptIn,
  getStopDestinationDetails,
} from '../../ducks';
import { proceed } from '../../proceed';
import { FlexDatesModal } from '../FlexDatesModal';
import { AddressFields } from './AddressFields';
import { AddressError } from './AddressFields/AddressError';
import { AddressInstructionsField } from './AddressFields/AddressInstructions';
import { ContactFields } from './ContactFields';
import { ContactSuspicionModal } from './ContactSuspicionModal';
import { FloorLevelSuspicionModal } from './FloorLevelSuspicionModal';
import { LocalityChangeModal } from './LocalityChangeModal';
import { validateEmail, validatePhone } from '../../../../utils/validation';

export const ContactStop: React.FC<RouteComponentProps> = ({ match }) => {
  const type = match.path.includes('pickup') ? StopType.PICKUP : StopType.DELIVERY;

  const titleTransTag = useTypedSelector(state =>
    getFlowTitleByStepAndSituation(state, type === StopType.PICKUP ? 'contactPickup' : 'contactDelivery')
  );
  const [forceShowErrors, setForceShowErrors] = React.useState(false);
  const [showAddressError, setShowAddressError] = React.useState(false);
  const [isAddressLoading, setIsAddressLoading] = React.useState(false);
  const [isFormError, setIsFormError] = React.useState(false);

  const pickupSituation = useTypedSelector(getPickupSituation);
  const showContact = type === StopType.DELIVERY || pickupSituation !== 'auction';
  const widgetAddress = useTypedSelector(state => getStopDestinationDetails(state, type));
  const existing = useTypedSelector(state => getContactPerson(type, state));
  const existingAddress = useTypedSelector(state => state.generalTransport.contact[type]?.address);
  /**
   * When Opp we required contact details for the pickup
   */
  const isProductPayment = useTypedSelector(getProductPaymentOptIn);
  const productPaymentAmount = useTypedSelector(getProductPaymentAmount);
  const isOppContact = type === StopType.PICKUP && isProductPayment;

  const queryData = useQueryClient();

  const submit = async (): Promise<void> => {
    // When trying to submit, but it isn't possible yet: force show errors
    setForceShowErrors(true);

    const hasContactError = !!(
      !existing?.details?.first_name ||
      validateEmail(existing?.details.email) ||
      validatePhone(existing?.details.phone)
    );

    // Is address loading || Has contact error
    if (isFormError || isAddressLoading || hasContactError) {
      return;
    }

    // Address is always required, but it could be not resolving
    if (!existingAddress) {
      // Let Sentry know
      const queries = queryData.getQueriesData({ queryKey: [CacheKey.RETRIEVE_GEO_LOCATION] });
      // Kept this error message the same, so it is easier to compare in Sentry
      // TODO: Should be updated to "take 3"
      logException('GF Contact Page - Address is not resolving', {
        queries,
        widgetAddress,
      });
      // Let the user know
      setShowAddressError(true);
      return;
    }

    // all good, proceed
    proceed();
  };

  return (
    <>
      <SheetLayoutHeader>
        <Heading size={2}>
          <Translate id={titleTransTag} />
        </Heading>
      </SheetLayoutHeader>
      <SectionWrapper centerContent={false} sectionStyle={'primary-light'} dashed={true}>
        <SheetLayoutContent>
          <AddressFields
            forceShowErrors={forceShowErrors}
            type={type}
            setLoading={setIsAddressLoading}
            setIsFormError={setIsFormError}
          />
          <div className="pb-[1em]">
            <AddressInstructionsField type={type} />
          </div>
          {showAddressError && (
            <div className="pb-[1em]">
              <AddressError />
            </div>
          )}
          {showContact && (
            <>
              {isOppContact && (
                <>
                  <h4 className="mb-2">
                    <Translate id={'request_flow.product_payment.bank_details'} />
                  </h4>
                  <div className="pb-[1em]">
                    <Translate
                      id={'request_flow.product_payment.bank_details_explain_with_opp'}
                      data={{
                        amount: priceAsString({
                          price: { amount: productPaymentAmount || undefined },
                          addPlusMinusIndicator: false,
                        }),
                      }}
                    />
                  </div>
                </>
              )}
              {!isOppContact && (
                <h4 className="mb-2">
                  <Translate id={'request_flow.contact.who_details'} />
                </h4>
              )}
              <ContactFields forceShowErrors={forceShowErrors} type={type} />
            </>
          )}
        </SheetLayoutContent>
      </SectionWrapper>
      <ContactSuspicionModal stopType={type} />
      <FloorLevelSuspicionModal stopType={type} />
      <LocalityChangeModal stopType={type} />
      <FlexDatesModal />
      <FooterLayout customSubmitFunction={submit} customIsLoading={isAddressLoading} />
    </>
  );
};
